import React, { useMemo, useState } from 'react';
import {
  FaHandshake,
  FaUsers,
  FaChartLine,
  FaCalendarCheck,
  FaMoneyBillWave,
  FaFilter,
  FaSearch,
  FaPlus,
  FaUserTie,
  FaStickyNote,
  FaCheckCircle,
  FaClock,
  FaTrash,
  FaLinkedin,
  FaInstagram,
  FaFacebook,
  FaWhatsapp,
  FaShareAlt,
  FaEdit,
  FaExternalLinkAlt,
  FaLayerGroup,
  FaTrophy,
  FaExclamationTriangle,
  FaChartPie,
  FaBullseye,
} from 'react-icons/fa';
import Modal from '../components/Modal';

const PIPELINE_STAGES = [
  {
    id: 'new',
    label: 'Novos Leads',
    description:
      'Primeiro contato com a oportunidade. O objetivo é entender o cenário e qualificar o lead.',
    badgeClass: 'bg-sky-100 text-sky-600',
    borderClass: 'border-sky-200',
    progressClass: 'bg-sky-400',
    defaultProbability: 0.18,
  },
  {
    id: 'qualification',
    label: 'Qualificação',
    description: 'Mapeando dores, orçamento e decisores para confirmar aderência ao que entregamos.',
    badgeClass: 'bg-blue-100 text-blue-600',
    borderClass: 'border-blue-200',
    progressClass: 'bg-blue-500',
    defaultProbability: 0.32,
  },
  {
    id: 'proposal',
    label: 'Proposta',
    description: 'Solução apresentada e proposta em avaliação. Acompanhando ajustes e objeções.',
    badgeClass: 'bg-indigo-100 text-indigo-600',
    borderClass: 'border-indigo-200',
    progressClass: 'bg-indigo-500',
    defaultProbability: 0.55,
  },
  {
    id: 'negotiation',
    label: 'Negociação',
    description: 'Detalhes finais alinhados, aprovação jurídica/financeira em andamento.',
    badgeClass: 'bg-purple-100 text-purple-600',
    borderClass: 'border-purple-200',
    progressClass: 'bg-purple-500',
    defaultProbability: 0.72,
  },
  {
    id: 'won',
    label: 'Ganhos recentes',
    description: 'Oportunidades concluídas com sucesso e prontas para onboarding.',
    badgeClass: 'bg-emerald-100 text-emerald-600',
    borderClass: 'border-emerald-200',
    progressClass: 'bg-emerald-500',
    defaultProbability: 1,
  },
];

const PIPELINE_STAGE_MAP = PIPELINE_STAGES.reduce((acc, stage) => {
  acc[stage.id] = stage;
  return acc;
}, {});

const CRM_TABS = [
  { id: 'overview', label: 'Visão geral' },
  { id: 'pipeline', label: 'Funil de vendas' },
  { id: 'accounts', label: 'Contas & relacionamento' },
  { id: 'integrations', label: 'Integrações & automações' },
  { id: 'reports', label: 'Insights & relatórios' },
];

const DEFAULT_STAGE_ID = PIPELINE_STAGES[0].id;

const SEGMENT_OPTIONS = ['Serviços', 'Tecnologia', 'Indústria', 'Varejo', 'Saúde'];

const getDefaultProbability = (stageId) =>
  PIPELINE_STAGE_MAP[stageId]?.defaultProbability ??
  PIPELINE_STAGE_MAP[DEFAULT_STAGE_ID].defaultProbability;

const SOCIAL_INTEGRATION_OPTIONS = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: FaLinkedin,
    description: 'Capture leads de formulários e mensagens privadas diretamente no funil.',
    audience: 'Ideal para relacionamento B2B e campanhas de geração de demanda.',
    loginUrl: 'https://www.linkedin.com/login',
    documentationUrl: 'https://www.linkedin.com/help/linkedin/answer/a507663',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: FaInstagram,
    description: 'Monitore respostas de stories e directs para engajar leads mais quentes.',
    audience: 'Fortaleça a presença de marca e responda rapidamente a interações.',
    loginUrl: 'https://www.instagram.com/accounts/login/',
    documentationUrl: 'https://developers.facebook.com/docs/instagram-basic-display-api',
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: FaFacebook,
    description: 'Sincronize formulários de anúncios e páginas para nutrir o pipeline.',
    audience: 'Centralize contatos captados em campanhas e comunidades.',
    loginUrl: 'https://www.facebook.com/login.php',
    documentationUrl: 'https://developers.facebook.com/docs/marketing-api/',
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    icon: FaWhatsapp,
    description: 'Integre conversas para registrar compromissos e histórico com clientes.',
    audience: 'Acompanhe tratativas de vendas consultivas em tempo real.',
    loginUrl: 'https://www.facebook.com/business/help/205587591547335',
    documentationUrl: 'https://developers.facebook.com/docs/whatsapp/cloud-api/',
  },
];

const EMPTY_FORM = {
  name: '',
  company: '',
  segment: SEGMENT_OPTIONS[0],
  owner: '',
  value: '',
  stage: DEFAULT_STAGE_ID,
  nextStep: '',
  nextStepDate: '',
  notes: '',
  cycleDays: '',
};

const EMPTY_INTEGRATION_FORM = {
  accountName: '',
  loginEmail: '',
  accessToken: '',
  webhookUrl: '',
};

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(numeric);
};

const formatDate = (value) => {
  if (!value) return 'Sem agenda';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem agenda';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

const formatDateTime = (value) => {
  if (!value) return 'Sem registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem registro';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isWithinDays = (value, days) => {
  if (!value) return false;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diff = (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= days;
};

const getDaysDiff = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const diff = (today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
  return Math.floor(diff);
};

const formatDaysAgo = (value) => {
  const diff = getDaysDiff(value);
  if (diff === null) return 'Sem registro';
  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Há 1 dia';
  return `Há ${diff} dias`;
};

const getQuarterLabel = (date) => {
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return `T${quarter} ${date.getFullYear()}`;
};

export default function CRM() {
  const [deals, setDeals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));
  const [editingDealId, setEditingDealId] = useState(null);
  const [socialIntegrations, setSocialIntegrations] = useState(() =>
    SOCIAL_INTEGRATION_OPTIONS.map((integration) => ({
      ...integration,
      connected: false,
      lastSync: null,
      connectionDetails: null,
    })),
  );
  const [integrationModal, setIntegrationModal] = useState({ open: false, integrationId: null });
  const [integrationForm, setIntegrationForm] = useState(() => ({ ...EMPTY_INTEGRATION_FORM }));

  const owners = useMemo(() => {
    const unique = new Set();
    deals.forEach((deal) => {
      if (deal.owner) {
        unique.add(deal.owner);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [deals]);

  const segments = useMemo(() => {
    const unique = new Set(SEGMENT_OPTIONS);
    deals.forEach((deal) => {
      if (deal.segment) {
        unique.add(deal.segment);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [deals]);

  const filteredDeals = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return deals.filter((deal) => {
      if (term) {
        const haystack = `${deal.name} ${deal.company} ${deal.owner}`.toLowerCase();
        if (!haystack.includes(term)) {
          return false;
        }
      }
      if (ownerFilter !== 'all' && deal.owner !== ownerFilter) {
        return false;
      }
      if (segmentFilter !== 'all' && deal.segment !== segmentFilter) {
        return false;
      }
      return true;
    });
  }, [deals, ownerFilter, searchTerm, segmentFilter]);

  const pipelineColumns = useMemo(
    () =>
      PIPELINE_STAGES.map((stage) => {
        const stageDeals = filteredDeals
          .filter((deal) => deal.stage === stage.id)
          .sort((a, b) => {
            if (!a.nextStepDate) return 1;
            if (!b.nextStepDate) return -1;
            return new Date(a.nextStepDate) - new Date(b.nextStepDate);
          });
        const totalValue = stageDeals.reduce((sum, deal) => sum + Number(deal.value || 0), 0);
        return {
          ...stage,
          deals: stageDeals,
          totalValue,
        };
      }),
    [filteredDeals],
  );

  const activeDealsCount = useMemo(
    () => filteredDeals.filter((deal) => deal.stage !== 'won').length,
    [filteredDeals],
  );

  const pipelineValue = useMemo(
    () =>
      filteredDeals
        .filter((deal) => deal.stage !== 'won')
        .reduce((sum, deal) => sum + Number(deal.value || 0), 0),
    [filteredDeals],
  );

  const weightedPipelineValue = useMemo(
    () =>
      filteredDeals
        .filter((deal) => deal.stage !== 'won')
        .reduce(
          (sum, deal) =>
            sum + Number(deal.value || 0) * (deal.probability ?? getDefaultProbability(deal.stage)),
          0,
        ),
    [filteredDeals],
  );

  const wonDealsValue = useMemo(
    () => deals.filter((deal) => deal.stage === 'won').reduce((sum, deal) => sum + Number(deal.value || 0), 0),
    [deals],
  );

  const wonRatio = useMemo(() => {
    const total = deals.length;
    if (!total) return 0;
    const wonCount = deals.filter((deal) => deal.stage === 'won').length;
    return Math.round((wonCount / total) * 100);
  }, [deals]);

  const averageCycle = useMemo(() => {
    const cycles = deals
      .map((deal) => Number(deal.cycleDays))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (!cycles.length) return null;
    return Math.round(cycles.reduce((sum, value) => sum + value, 0) / cycles.length);
  }, [deals]);

  const dealsCreatedThisMonth = useMemo(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    return deals.filter((deal) => {
      if (!deal.createdAt) return false;
      const created = new Date(deal.createdAt);
      return created.getMonth() === month && created.getFullYear() === year;
    }).length;
  }, [deals]);

  const wonThisMonthValue = useMemo(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    return deals
      .filter((deal) => deal.stage === 'won' && deal.updatedAt)
      .filter((deal) => {
        const updated = new Date(deal.updatedAt);
        return updated.getMonth() === month && updated.getFullYear() === year;
      })
      .reduce((sum, deal) => sum + Number(deal.value || 0), 0);
  }, [deals]);

  const upcomingActivities = useMemo(
    () =>
      filteredDeals
        .filter((deal) => deal.stage !== 'won' && isWithinDays(deal.nextStepDate, 7))
        .sort((a, b) => new Date(a.nextStepDate) - new Date(b.nextStepDate)),
    [filteredDeals],
  );

  const focusDeals = useMemo(
    () =>
      filteredDeals
        .filter((deal) => deal.stage !== 'won')
        .sort((a, b) => b.probability - a.probability || b.value - a.value)
        .slice(0, 4),
    [filteredDeals],
  );

  const recentWins = useMemo(
    () =>
      deals
        .filter((deal) => deal.stage === 'won')
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
        .slice(0, 4),
    [deals],
  );

  const staleDeals = useMemo(() => {
    const today = new Date();
    return filteredDeals
      .filter((deal) => {
        if (deal.stage === 'won') return false;
        if (!deal.updatedAt) return true;
        const updated = new Date(deal.updatedAt);
        if (Number.isNaN(updated.getTime())) return false;
        const diff = (today.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24);
        return diff >= 14;
      })
      .sort((a, b) => new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0))
      .slice(0, 5);
  }, [filteredDeals]);

  const connectedIntegrations = useMemo(
    () => socialIntegrations.filter((integration) => integration.connected),
    [socialIntegrations],
  );

  const pipelineStageHealth = useMemo(
    () =>
      pipelineColumns.map((column) => {
        const withoutNextStep = column.deals.filter((deal) => !deal.nextStep).length;
        const overdue = column.deals.filter((deal) => {
          if (!deal.nextStepDate) return false;
          const next = new Date(deal.nextStepDate);
          if (Number.isNaN(next.getTime())) return false;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          next.setHours(0, 0, 0, 0);
          return next < today;
        }).length;
        const avgProbability = column.deals.length
          ? Math.round(
              (column.deals.reduce(
                (sum, deal) => sum + (deal.probability ?? getDefaultProbability(deal.stage)),
                0,
              ) /
                column.deals.length) *
                100,
            )
          : 0;
        return {
          id: column.id,
          label: column.label,
          count: column.deals.length,
          totalValue: column.totalValue,
          overdue,
          withoutNextStep,
          avgProbability,
        };
      }),
    [pipelineColumns],
  );

  const stageAlerts = useMemo(
    () => pipelineStageHealth.filter((stage) => stage.overdue > 0 || stage.withoutNextStep > 0),
    [pipelineStageHealth],
  );

  const accountSummaries = useMemo(() => {
    const map = new Map();
    filteredDeals.forEach((deal) => {
      const key = deal.company || 'Conta sem nome';
      if (!map.has(key)) {
        map.set(key, {
          company: key,
          opportunities: 0,
          totalValue: 0,
          owners: new Set(),
          nextStepDate: null,
          latestStage: deal.stage,
          segment: deal.segment || 'Não informado',
          won: 0,
        });
      }
      const entry = map.get(key);
      entry.opportunities += 1;
      entry.totalValue += Number(deal.value || 0);
      if (deal.owner) {
        entry.owners.add(deal.owner);
      }
      if (deal.stage === 'won') {
        entry.won += 1;
      }
      if (deal.nextStepDate) {
        const current = entry.nextStepDate ? new Date(entry.nextStepDate) : null;
        const next = new Date(deal.nextStepDate);
        if (!current || (Number.isFinite(next.getTime()) && next < current)) {
          entry.nextStepDate = deal.nextStepDate;
        }
      }
      entry.latestStage = deal.stage;
    });

    return Array.from(map.values())
      .map((entry) => ({
        ...entry,
        owners: Array.from(entry.owners).sort((a, b) => a.localeCompare(b, 'pt-BR')),
        winRate: entry.opportunities ? Math.round((entry.won / entry.opportunities) * 100) : 0,
      }))
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 8);
  }, [filteredDeals]);

  const teamPerformance = useMemo(() => {
    const map = new Map();
    filteredDeals.forEach((deal) => {
      const owner = deal.owner || 'Sem responsável';
      if (!map.has(owner)) {
        map.set(owner, {
          owner,
          opportunities: 0,
          totalValue: 0,
          won: 0,
          wonValue: 0,
          cycles: [],
          upcoming: 0,
        });
      }
      const entry = map.get(owner);
      entry.opportunities += 1;
      entry.totalValue += Number(deal.value || 0);
      if (deal.stage === 'won') {
        entry.won += 1;
        entry.wonValue += Number(deal.value || 0);
      }
      if (deal.cycleDays) {
        entry.cycles.push(Number(deal.cycleDays));
      }
      if (isWithinDays(deal.nextStepDate, 7)) {
        entry.upcoming += 1;
      }
    });

    return Array.from(map.values())
      .map((entry) => ({
        ...entry,
        winRate: entry.opportunities ? Math.round((entry.won / entry.opportunities) * 100) : 0,
        avgCycle: entry.cycles.length
          ? Math.round(entry.cycles.reduce((sum, value) => sum + value, 0) / entry.cycles.length)
          : null,
      }))
      .sort((a, b) => b.totalValue - a.totalValue);
  }, [filteredDeals]);

  const coverageBySegment = useMemo(() => {
    const map = new Map();
    filteredDeals.forEach((deal) => {
      const segment = deal.segment || 'Sem segmento';
      if (!map.has(segment)) {
        map.set(segment, { segment, count: 0, value: 0 });
      }
      const entry = map.get(segment);
      entry.count += 1;
      entry.value += Number(deal.value || 0);
    });
    return Array.from(map.values()).sort((a, b) => b.value - a.value);
  }, [filteredDeals]);

  const forecastByQuarter = useMemo(() => {
    const map = new Map();
    filteredDeals
      .filter((deal) => deal.stage !== 'won')
      .forEach((deal) => {
        let key = 'Sem previsão';
        if (deal.nextStepDate) {
          const date = new Date(deal.nextStepDate);
          if (!Number.isNaN(date.getTime())) {
            key = getQuarterLabel(date);
          }
        }
        if (!map.has(key)) {
          map.set(key, { label: key, weighted: 0, total: 0, deals: 0 });
        }
        const entry = map.get(key);
        const probability = deal.probability ?? getDefaultProbability(deal.stage);
        entry.weighted += Number(deal.value || 0) * probability;
        entry.total += Number(deal.value || 0);
        entry.deals += 1;
      });
    return Array.from(map.values()).sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [filteredDeals]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'active',
        label: 'Oportunidades ativas',
        value: activeDealsCount,
        detail:
          pipelineColumns
            .filter((column) => column.deals.length)
            .map((column) => `${column.deals.length} em ${column.label}`)
            .join(' • ') || 'Nenhuma oportunidade em andamento',
        icon: <FaHandshake className="text-sky-500" />,
      },
      {
        id: 'value',
        label: 'Valor em negociação',
        value: formatCurrency(pipelineValue),
        detail: `Previsão ponderada: ${formatCurrency(weightedPipelineValue)}`,
        icon: <FaMoneyBillWave className="text-emerald-500" />,
      },
      {
        id: 'conversion',
        label: 'Taxa de conversão',
        value: `${wonRatio}%`,
        detail: averageCycle
          ? `Ciclo médio de ${averageCycle} dias`
          : 'Cadastre mais dados para medir o ciclo médio',
        icon: <FaChartLine className="text-indigo-500" />,
      },
      {
        id: 'agenda',
        label: 'Próximos passos',
        value: upcomingActivities.length,
        detail:
          upcomingActivities.length > 0
            ? `${upcomingActivities.length} compromissos nos próximos 7 dias`
            : 'Sem pendências para os próximos dias',
        icon: <FaCalendarCheck className="text-purple-500" />,
      },
      {
        id: 'newDeals',
        label: 'Novas oportunidades (mês)',
        value: dealsCreatedThisMonth,
        detail: `Ganhos no período: ${formatCurrency(wonThisMonthValue)}`,
        icon: <FaBullseye className="text-amber-500" />,
      },
    ],
    [
      activeDealsCount,
      averageCycle,
      dealsCreatedThisMonth,
      pipelineColumns,
      pipelineValue,
      upcomingActivities.length,
      weightedPipelineValue,
      wonRatio,
      wonThisMonthValue,
    ],
  );

  const handleStageChange = (dealId, stageId) => {
    setDeals((prev) =>
      prev.map((deal) => {
        if (deal.id !== dealId) {
          return deal;
        }
        return {
          ...deal,
          stage: stageId,
          probability: getDefaultProbability(stageId),
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  };

  const openModal = () => {
    setEditingDealId(null);
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  };

  const openEditModal = (deal) => {
    setEditingDealId(deal.id);
    setForm({
      name: deal.name || '',
      company: deal.company || '',
      segment: deal.segment || SEGMENT_OPTIONS[0],
      owner: deal.owner || '',
      value: deal.value ?? '',
      stage: deal.stage || DEFAULT_STAGE_ID,
      nextStep: deal.nextStep || '',
      nextStepDate: deal.nextStepDate ? deal.nextStepDate.slice(0, 10) : '',
      notes: deal.notes || '',
      cycleDays: deal.cycleDays ?? '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm({ ...EMPTY_FORM });
    setEditingDealId(null);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedName = form.name.trim();
    const trimmedCompany = form.company.trim();
    if (!trimmedName || !trimmedCompany) {
      return;
    }

    const value = Number(form.value);

    const baseDeal = {
      name: trimmedName,
      company: trimmedCompany,
      segment: form.segment,
      owner: form.owner.trim() || 'Não informado',
      value: Number.isFinite(value) ? value : 0,
      stage: form.stage,
      probability: getDefaultProbability(form.stage),
      cycleDays: Number.isFinite(Number(form.cycleDays)) && Number(form.cycleDays) > 0 ? Number(form.cycleDays) : null,
      nextStep: form.nextStep.trim(),
      nextStepDate: form.nextStepDate || null,
      notes: form.notes.trim(),
      updatedAt: new Date().toISOString(),
    };

    if (editingDealId) {
      setDeals((prev) =>
        prev.map((deal) =>
          deal.id === editingDealId
            ? {
                ...deal,
                ...baseDeal,
              }
            : deal,
        ),
      );
    } else {
      const newDeal = {
        id: `OPP-${Date.now()}`,
        ...baseDeal,
        createdAt: new Date().toISOString(),
      };

      setDeals((prev) => [...prev, newDeal]);
    }

    closeModal();
  };

  const handleDeleteDeal = (dealId) => {
    const deal = deals.find((item) => item.id === dealId);
    if (!deal) {
      return;
    }

    const shouldDelete = window.confirm(
      `Deseja excluir a oportunidade "${deal.name}"? Esta ação não pode ser desfeita.`,
    );

    if (!shouldDelete) {
      return;
    }

    setDeals((prev) => prev.filter((item) => item.id !== dealId));
    if (editingDealId === dealId) {
      closeModal();
    }
  };

  const syncIntegrationNow = (integrationId) => {
    setSocialIntegrations((prev) =>
      prev.map((integration) =>
        integration.id === integrationId && integration.connected
          ? {
              ...integration,
              lastSync: new Date().toISOString(),
            }
          : integration,
      ),
    );
  };

  const openIntegrationModal = (integrationId) => {
    const integration = socialIntegrations.find((item) => item.id === integrationId);
    if (!integration) {
      return;
    }

    setIntegrationModal({ open: true, integrationId });
    setIntegrationForm({ ...EMPTY_INTEGRATION_FORM, ...(integration.connectionDetails || {}) });
  };

  const closeIntegrationModal = () => {
    setIntegrationModal({ open: false, integrationId: null });
    setIntegrationForm({ ...EMPTY_INTEGRATION_FORM });
  };

  const handleIntegrationFormChange = (event) => {
    const { name, value } = event.target;
    setIntegrationForm((prev) => ({ ...prev, [name]: value }));
  };

  const openProviderAuth = (integrationId) => {
    const integration = socialIntegrations.find((item) => item.id === integrationId);
    if (!integration?.loginUrl || typeof window === 'undefined') {
      return;
    }

    window.open(integration.loginUrl, '_blank', 'noopener,noreferrer');
  };

  const submitIntegrationConnection = (event) => {
    event.preventDefault();
    if (!integrationModal.integrationId) {
      return;
    }

    const trimmedAccount = integrationForm.accountName.trim();
    const trimmedToken = integrationForm.accessToken.trim();

    if (!trimmedAccount || !trimmedToken) {
      return;
    }

    const trimmedEmail = integrationForm.loginEmail.trim();
    const trimmedWebhook = integrationForm.webhookUrl.trim();

    setSocialIntegrations((prev) =>
      prev.map((integration) =>
        integration.id === integrationModal.integrationId
          ? {
              ...integration,
              connected: true,
              lastSync: new Date().toISOString(),
              connectionDetails: {
                accountName: trimmedAccount,
                loginEmail: trimmedEmail,
                accessToken: trimmedToken,
                webhookUrl: trimmedWebhook,
              },
            }
          : integration,
      ),
    );

    closeIntegrationModal();
  };

  const disconnectIntegration = (integrationId) => {
    const integration = socialIntegrations.find((item) => item.id === integrationId);
    if (!integration?.connected) {
      return;
    }

    const shouldDisconnect = window.confirm(
      `Deseja desconectar o canal ${integration.name}? Você poderá reconectar quando quiser.`,
    );

    if (!shouldDisconnect) {
      return;
    }

    setSocialIntegrations((prev) =>
      prev.map((item) =>
        item.id === integrationId
          ? {
              ...item,
              connected: false,
              lastSync: null,
              connectionDetails: null,
            }
          : item,
      ),
    );
  };

  const formatSyncDate = (value) => {
    if (!value) return 'Nunca sincronizado';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Nunca sincronizado';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isEditingDeal = Boolean(editingDealId);
  const activeIntegration = integrationModal.integrationId
    ? socialIntegrations.find((item) => item.id === integrationModal.integrationId)
    : null;
  const isManagingIntegration = Boolean(activeIntegration?.connected);
  const overviewTabContent = (
    <div className="space-y-8">
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {summaryCards.map((card) => (
          <article
            key={card.id}
            className="flex flex-col justify-between rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm ring-1 ring-slate-100 transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-lg">
                {card.icon}
              </span>
              <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Resumo</span>
            </div>
            <div className="mt-6 space-y-2">
              <p className="text-sm font-medium text-slate-500">{card.label}</p>
              <p className="text-3xl font-semibold text-slate-900">
                {typeof card.value === 'number' ? card.value : card.value}
              </p>
              <p className="text-sm text-slate-500">{card.detail}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="grid gap-6 xl:grid-cols-3">
        <article className="xl:col-span-2 rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-slate-900">Agenda de relacionamento</h3>
              <p className="text-sm text-slate-500">
                Priorize follow-ups e mantenha a proximidade com cada conta estratégica.
              </p>
            </div>
            <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-600">
              {upcomingActivities.length} próximos passos
            </span>
          </header>

          <div className="mt-5 space-y-3">
            {upcomingActivities.length ? (
              upcomingActivities.map((deal) => (
                <div
                  key={deal.id}
                  className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {deal.company} • {deal.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {deal.nextStep || 'Defina o próximo passo para manter o ritmo da negociação.'}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-2">
                        <FaUserTie className="text-slate-400" /> {deal.owner}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <FaClock className="text-slate-400" /> {formatDate(deal.nextStepDate)}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <FaCheckCircle className="text-slate-400" /> {PIPELINE_STAGE_MAP[deal.stage]?.label}
                      </span>
                    </div>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500">
                    {formatDaysAgo(deal.updatedAt)}
                  </span>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                Nenhum compromisso previsto para os próximos 7 dias. Aproveite para nutrir relacionamentos.
              </div>
            )}
          </div>
        </article>

        <article className="space-y-4 rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Saúde do funil</h3>
              <p className="text-sm text-slate-500">Monitoramento por estágio e gargalos de execução.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
              <FaLayerGroup /> {pipelineStageHealth.reduce((sum, stage) => sum + stage.count, 0)} deals
            </span>
          </header>

          <div className="space-y-3">
            {pipelineStageHealth.map((stage) => (
              <div key={stage.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between text-sm font-medium text-slate-700">
                  <span>{stage.label}</span>
                  <span>{stage.count} oportunidades</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-white">
                  <div
                    className={`h-2 rounded-full ${PIPELINE_STAGE_MAP[stage.id]?.progressClass || 'bg-sky-400'}`}
                    style={{ width: `${Math.min(100, stage.avgProbability)}%` }}
                  />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span>Valor: {formatCurrency(stage.totalValue)}</span>
                  <span>Prob. média: {stage.avgProbability}%</span>
                  <span>Atrasos: {stage.overdue}</span>
                  <span>Sem próxima ação: {stage.withoutNextStep}</span>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>
      <section className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm xl:col-span-1">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Oportunidades em foco</h3>
              <p className="text-sm text-slate-500">As negociações mais quentes para acompanhar de perto.</p>
            </div>
            <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-600">Top {focusDeals.length}</span>
          </header>

          <div className="mt-4 space-y-3">
            {focusDeals.length ? (
              focusDeals.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{deal.name}</p>
                      <p className="text-xs text-slate-500">{deal.company}</p>
                    </div>
                    <span className="text-sm font-semibold text-slate-600">{formatCurrency(deal.value)}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <FaUserTie className="text-slate-400" /> {deal.owner}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaCheckCircle className="text-slate-400" />
                      {PIPELINE_STAGE_MAP[deal.stage]?.label}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaChartLine className="text-slate-400" /> {Math.round((deal.probability ?? 0) * 100)}%
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Cadastre novas oportunidades para gerar recomendações estratégicas.
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Risco de estagnação</h3>
              <p className="text-sm text-slate-500">Negócios há mais de 14 dias sem atualização.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-600">
              <FaExclamationTriangle /> {staleDeals.length}
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {staleDeals.length ? (
              staleDeals.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{deal.name}</p>
                      <p className="text-xs text-slate-500">{deal.company}</p>
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500">
                      {formatDaysAgo(deal.updatedAt)}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <FaUserTie className="text-slate-400" /> {deal.owner}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaCheckCircle className="text-slate-400" /> {PIPELINE_STAGE_MAP[deal.stage]?.label}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaCalendarCheck className="text-slate-400" /> {formatDate(deal.nextStepDate)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Nenhum alerta de estagnação com os filtros atuais.
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Ganhos recentes</h3>
              <p className="text-sm text-slate-500">Histórico das vitórias para celebrar com o time.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600">
              <FaTrophy /> {recentWins.length}
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {recentWins.length ? (
              recentWins.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{deal.company}</p>
                      <p className="text-xs text-slate-500">{deal.name}</p>
                    </div>
                    <span className="text-sm font-semibold text-emerald-600">{formatCurrency(deal.value)}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <FaUserTie className="text-slate-400" /> {deal.owner}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <FaCalendarCheck className="text-slate-400" /> {formatDate(deal.updatedAt || deal.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Ainda não há oportunidades ganhas com os filtros selecionados.
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.5fr,1fr]">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Previsão por trimestre</h3>
              <p className="text-sm text-slate-500">Combine probabilidade e agenda para estimar o volume futuro.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              <FaChartLine /> {formatCurrency(weightedPipelineValue)}
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {forecastByQuarter.length ? (
              forecastByQuarter.map((item) => (
                <div key={item.label} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                  <div className="space-y-1">
                    <p className="font-semibold text-slate-900">{item.label}</p>
                    <p className="text-xs text-slate-500">{item.deals} oportunidades planejadas</p>
                  </div>
                  <div className="text-right text-sm text-slate-600">
                    <p>Valor total: {formatCurrency(item.total)}</p>
                    <p className="text-xs text-slate-500">Previsão: {formatCurrency(item.weighted)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Cadastre datas de próximos passos para gerar projeções de receita.
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Cobertura por segmento</h3>
              <p className="text-sm text-slate-500">Distribuição das oportunidades por mercado alvo.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              <FaChartPie /> {coverageBySegment.length} segmentos
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {coverageBySegment.length ? (
              coverageBySegment.map((segment) => (
                <div key={segment.segment} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                  <div>
                    <p className="font-semibold text-slate-900">{segment.segment}</p>
                    <p className="text-xs text-slate-500">{segment.count} oportunidades</p>
                  </div>
                  <p className="text-sm font-medium text-slate-600">{formatCurrency(segment.value)}</p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Nenhuma oportunidade encontrada com os filtros aplicados.
              </div>
            )}
          </div>
        </article>
      </section>
    </div>
  );
  const pipelineTabContent = (
    <div className="space-y-8">
      <section className="grid gap-4 md:grid-cols-3">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Cobertura do funil</h3>
              <p className="text-sm text-slate-500">Valor potencial distribuído nas etapas ativas.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              {activeDealsCount} oportunidades
            </span>
          </div>
          <p className="mt-4 text-3xl font-semibold text-slate-900">{formatCurrency(pipelineValue)}</p>
          <p className="text-sm text-slate-500">
            Previsão ponderada de {formatCurrency(weightedPipelineValue)} considerando probabilidade atual.
          </p>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Alertas operacionais</h3>
              <p className="text-sm text-slate-500">Negócios que exigem atenção imediata.</p>
            </div>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-600">
              {stageAlerts.length} alertas
            </span>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            {stageAlerts.length ? (
              stageAlerts.map((stage) => (
                <li key={stage.id} className="rounded-2xl bg-slate-50 px-4 py-2">
                  {stage.label}: {stage.overdue} atrasos • {stage.withoutNextStep} sem próximo passo
                </li>
              ))
            ) : (
              <li className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-center text-sm text-slate-500">
                Nenhum alerta com os filtros atuais.
              </li>
            )}
          </ul>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Desempenho recente</h3>
              <p className="text-sm text-slate-500">Acompanhe ganhos e novas oportunidades no mês.</p>
            </div>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600">
              {formatCurrency(wonThisMonthValue)} ganhos
            </span>
          </div>
          <div className="mt-4 grid gap-2 text-sm text-slate-600">
            <span>Novas oportunidades: {dealsCreatedThisMonth}</span>
            <span>Taxa de conversão geral: {wonRatio}%</span>
            <span>Ciclo médio: {averageCycle ? `${averageCycle} dias` : 'Cadastre mais dados'}</span>
          </div>
        </article>
      </section>

      <section className="space-y-6">
        <header className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-slate-900">Funil de vendas</h2>
            <p className="text-sm text-slate-500">
              Arraste e atualize o estágio conforme os avanços e acompanhe o potencial de receita em cada fase.
            </p>
          </div>
          <p className="text-sm font-medium text-slate-500">
            Valor potencial em negociação: <span className="text-slate-900">{formatCurrency(pipelineValue)}</span>
          </p>
        </header>

        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {pipelineColumns.map((column) => (
            <div
              key={column.id}
              className={`flex flex-col rounded-3xl border bg-white/90 p-4 ${column.borderClass} shadow-sm`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{column.label}</h3>
                  <p className="mt-1 text-xs text-slate-500">{column.description}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${column.badgeClass}`}>
                  {formatCurrency(column.totalValue)}
                </span>
              </div>

              <div className="mt-4 space-y-3">
                {column.deals.length ? (
                  column.deals.map((deal) => (
                    <article key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">{deal.name}</h4>
                          <p className="text-xs text-slate-500">{deal.company}</p>
                        </div>
                        <span className="text-sm font-semibold text-slate-600">{formatCurrency(deal.value)}</span>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-2">
                          <FaUserTie className="text-slate-400" />
                          {deal.owner}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <FaCalendarCheck className="text-slate-400" />
                          {formatDate(deal.nextStepDate)}
                        </span>
                      </div>

                      <div className="mt-3">
                        <label className="text-xs font-medium text-slate-500" htmlFor={`stage-${deal.id}`}>
                          Etapa atual
                        </label>
                        <select
                          id={`stage-${deal.id}`}
                          value={deal.stage}
                          onChange={(event) => handleStageChange(deal.id, event.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                        >
                          {PIPELINE_STAGES.map((stage) => (
                            <option key={stage.id} value={stage.id}>
                              {stage.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {deal.nextStep ? (
                        <p className="mt-3 inline-flex w-full items-start gap-2 rounded-xl bg-white px-3 py-2 text-xs text-slate-600 shadow-sm">
                          <FaStickyNote className="mt-0.5 text-slate-400" /> {deal.nextStep}
                        </p>
                      ) : null}

                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>Probabilidade</span>
                          <span>{Math.round((deal.probability ?? 0) * 100)}%</span>
                        </div>
                        <div className="mt-1 h-2 w-full rounded-full bg-slate-200">
                          <div
                            className={`h-2 rounded-full ${PIPELINE_STAGE_MAP[deal.stage]?.progressClass || 'bg-sky-400'}`}
                            style={{ width: `${Math.min(100, Math.round((deal.probability ?? 0) * 100))}%` }}
                          />
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                        <span className="text-slate-400">Atualizado {formatDateTime(deal.updatedAt)}</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(deal)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                          >
                            <FaEdit /> Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteDeal(deal.id)}
                            className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-3 py-1 font-medium text-rose-500 transition hover:border-rose-300 hover:bg-rose-50"
                          >
                            <FaTrash /> Excluir
                          </button>
                        </div>
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                    Nenhuma oportunidade nesta etapa com os filtros atuais.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
  const accountsTabContent = (
    <div className="space-y-8">
      <section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white/90 shadow-sm">
          <header className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Contas estratégicas</h3>
              <p className="text-sm text-slate-500">Organize as principais empresas e acompanhe os responsáveis.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              {accountSummaries.length} contas
            </span>
          </header>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-6 py-3 font-medium">Conta</th>
                  <th className="px-6 py-3 font-medium">Segmento</th>
                  <th className="px-6 py-3 font-medium">Valor potencial</th>
                  <th className="px-6 py-3 font-medium">Responsáveis</th>
                  <th className="px-6 py-3 font-medium">Próximo passo</th>
                  <th className="px-6 py-3 font-medium">Taxa de ganho</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {accountSummaries.length ? (
                  accountSummaries.map((account) => (
                    <tr key={account.company}>
                      <td className="px-6 py-4 text-slate-900">
                        <div className="font-semibold">{account.company}</div>
                        <div className="text-xs text-slate-500">{account.opportunities} oportunidades</div>
                      </td>
                      <td className="px-6 py-4">{account.segment}</td>
                      <td className="px-6 py-4 font-medium text-slate-700">{formatCurrency(account.totalValue)}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                          {account.owners.length ? account.owners.join(', ') : 'Não informado'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-500">
                        {account.nextStepDate ? formatDate(account.nextStepDate) : 'Sem data definida'}
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-700">{account.winRate}%</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-sm text-slate-500">
                      Nenhuma conta encontrada com os filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <div className="space-y-6">
          <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
            <header className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Performance da equipe</h3>
                <p className="text-sm text-slate-500">Compare volume, ganhos e compromissos por responsável.</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {teamPerformance.length} membros
              </span>
            </header>

            <div className="mt-4 space-y-3">
              {teamPerformance.length ? (
                teamPerformance.map((member) => (
                  <div key={member.owner} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{member.owner}</p>
                        <p className="text-xs text-slate-500">
                          {member.opportunities} oportunidades • {formatCurrency(member.totalValue)}
                        </p>
                      </div>
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600">
                        {member.winRate}% win rate
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span>Ganhos: {formatCurrency(member.wonValue)}</span>
                      <span>Ciclo médio: {member.avgCycle ? `${member.avgCycle} dias` : '—'}</span>
                      <span>Próximos 7 dias: {member.upcoming}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Aplique filtros menos restritivos para visualizar o desempenho da equipe.
                </div>
              )}
            </div>
          </article>

          <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Agenda crítica</h3>
            <p className="text-sm text-slate-500">Próximos passos prioritários por conta.</p>
            <div className="mt-4 space-y-3">
              {upcomingActivities.length ? (
                upcomingActivities.slice(0, 5).map((deal) => (
                  <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-900">{deal.company}</span>
                      <span className="text-xs text-slate-500">{formatDate(deal.nextStepDate)}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{deal.nextStep || 'Defina uma próxima ação.'}</p>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                  Nenhum compromisso encontrado nos próximos 7 dias.
                </div>
              )}
            </div>
          </article>
        </div>
      </section>
    </div>
  );
  const integrationsTabContent = (
    <div className="space-y-8">
      <section className="grid gap-4 md:grid-cols-3">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Integrações conectadas</h3>
          <p className="mt-2 text-sm text-slate-500">
            Amplie o funil capturando leads diretamente dos canais mais relevantes para o seu negócio.
          </p>
          <div className="mt-4 flex items-center gap-4">
            <div className="text-3xl font-semibold text-slate-900">{connectedIntegrations.length}</div>
            <div className="text-sm text-slate-500">
              de {socialIntegrations.length} canais disponíveis já estão ativos.
            </div>
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Próxima ação</h3>
          <p className="mt-2 text-sm text-slate-500">
            Defina automações para converter interações em novas oportunidades e notificações para o time.
          </p>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li className="rounded-2xl bg-slate-50 px-4 py-2">• Crie regras por palavra-chave para leads quentes.</li>
            <li className="rounded-2xl bg-slate-50 px-4 py-2">• Envie mensagens urgentes para o responsável certo.</li>
            <li className="rounded-2xl bg-slate-50 px-4 py-2">• Compare performance por canal e ajuste campanhas.</li>
          </ul>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Últimas sincronizações</h3>
          <p className="mt-2 text-sm text-slate-500">Verifique quais canais enviaram dados recentemente.</p>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            {socialIntegrations.map((integration) => (
              <li key={integration.id} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-2">
                <span>{integration.name}</span>
                <span className="text-xs text-slate-500">{formatSyncDate(integration.lastSync)}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        {socialIntegrations.map((integration) => {
          const Icon = integration.icon;
          return (
            <div
              key={integration.id}
              className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm lg:flex-row lg:items-start lg:justify-between"
            >
              <div className="flex items-start gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-xl text-sky-500">
                  <Icon />
                </span>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-slate-900">{integration.name}</p>
                  <p className="text-xs text-slate-500">{integration.description}</p>
                  <p className="text-xs font-medium text-slate-500">{integration.audience}</p>
                  <p className="text-xs text-slate-400">
                    Status: {integration.connected ? 'Conectado' : 'Não conectado'} • {formatSyncDate(integration.lastSync)}
                  </p>
                  {integration.connected && integration.connectionDetails ? (
                    <p className="text-xs text-slate-500">
                      Conta: <span className="font-semibold">{integration.connectionDetails.accountName}</span>
                      {integration.connectionDetails.loginEmail ? ` • ${integration.connectionDetails.loginEmail}` : ''}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-2 lg:items-end">
                <button
                  type="button"
                  onClick={() => openIntegrationModal(integration.id)}
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200 ${
                    integration.connected
                      ? 'bg-white text-slate-600 shadow-sm hover:bg-slate-100'
                      : 'bg-sky-500 text-white shadow-sm hover:bg-sky-600'
                  }`}
                >
                  {integration.connected ? 'Gerenciar conexão' : 'Conectar canal'}
                </button>
                {integration.connected ? (
                  <div className="flex flex-col gap-2 lg:items-end">
                    <button
                      type="button"
                      onClick={() => syncIntegrationNow(integration.id)}
                      className="inline-flex items-center justify-center rounded-full border border-sky-500 px-4 py-2 text-xs font-medium text-sky-600 transition hover:bg-sky-50"
                    >
                      Sincronizar agora
                    </button>
                    <button
                      type="button"
                      onClick={() => disconnectIntegration(integration.id)}
                      className="inline-flex items-center justify-center rounded-full border border-rose-200 px-4 py-2 text-xs font-medium text-rose-500 transition hover:border-rose-300 hover:bg-rose-50"
                    >
                      Desconectar
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
  const reportsTabContent = (
    <div className="space-y-8">
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Receita prevista</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{formatCurrency(weightedPipelineValue)}</p>
          <p className="text-sm text-slate-500">Valor ponderado das oportunidades em negociação.</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Ganhos recentes</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{formatCurrency(wonDealsValue)}</p>
          <p className="text-sm text-slate-500">Soma das oportunidades marcadas como ganhas.</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Alertas ativos</p>
          <p className="mt-3 text-3xl font-semibold text-amber-600">{stageAlerts.length}</p>
          <p className="text-sm text-slate-500">Estágios com atrasos ou sem próxima ação.</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Integrações</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900">{connectedIntegrations.length}</p>
          <p className="text-sm text-slate-500">Canais conectados enviando leads automaticamente.</p>
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <header className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Alertas por etapa</h3>
              <p className="text-sm text-slate-500">Gargalos identificados no funil para ação imediata.</p>
            </div>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-600">
              {stageAlerts.length} alertas
            </span>
          </header>

          <div className="mt-4 space-y-3">
            {stageAlerts.length ? (
              stageAlerts.map((stage) => (
                <div key={stage.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900">{stage.label}</p>
                    <span className="text-xs text-slate-500">{stage.count} oportunidades</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span>Atrasos: {stage.overdue}</span>
                    <span>Sem próxima ação: {stage.withoutNextStep}</span>
                    <span>Probabilidade média: {stage.avgProbability}%</span>
                    <span>Valor: {formatCurrency(stage.totalValue)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Nenhum alerta identificado – ótimo trabalho!
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Leads a resgatar</h3>
          <p className="text-sm text-slate-500">Oportunidades com maior risco de perda.</p>
          <div className="mt-4 space-y-3">
            {staleDeals.length ? (
              staleDeals.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-900">{deal.company}</span>
                    <span className="text-xs text-slate-500">{formatDaysAgo(deal.updatedAt)}</span>
                  </div>
                  <p className="text-xs text-slate-500">{deal.name}</p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                Sem riscos mapeados neste momento.
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Desempenho por responsável</h3>
          <p className="text-sm text-slate-500">Distribuição da carteira e ganho por consultor.</p>
          <div className="mt-4 space-y-3">
            {teamPerformance.length ? (
              teamPerformance.map((member) => (
                <div key={member.owner} className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">{member.owner}</span>
                    <span className="text-xs text-slate-500">{formatCurrency(member.totalValue)}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-white">
                    <div
                      className="h-2 rounded-full bg-sky-500"
                      style={{ width: `${Math.min(100, member.winRate)}%` }}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span>Win rate: {member.winRate}%</span>
                    <span>Ganhos: {formatCurrency(member.wonValue)}</span>
                    <span>Ciclo médio: {member.avgCycle ? `${member.avgCycle} dias` : '—'}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Nenhum dado disponível para gerar o relatório da equipe.
              </div>
            )}
          </div>
        </article>

        <article className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Vitórias recentes</h3>
          <p className="text-sm text-slate-500">Registre os aprendizados dos negócios concluídos.</p>
          <div className="mt-4 space-y-3">
            {recentWins.length ? (
              recentWins.map((deal) => (
                <div key={deal.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-900">{deal.company}</span>
                    <span className="text-xs text-slate-500">{formatDate(deal.updatedAt || deal.createdAt)}</span>
                  </div>
                  <p className="text-xs text-slate-500">{deal.name}</p>
                  <p className="text-xs font-medium text-emerald-600">{formatCurrency(deal.value)}</p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                Cadastre oportunidades ganhas para alimentar este painel.
              </div>
            )}
          </div>
        </article>
      </section>
    </div>
  );
  return (
    <div className="space-y-10 bg-gradient-to-b from-slate-50 via-white to-white p-6 lg:p-10">
      <header className="rounded-4xl relative overflow-hidden border border-slate-200 bg-white/80 p-6 shadow-sm lg:p-10">
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-sky-100 via-indigo-100 to-slate-100 opacity-70" />
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-1 text-sm font-medium text-sky-600 shadow-sm">
              <FaHandshake /> CRM inteligente
            </div>
            <h1 className="text-3xl font-semibold text-slate-900 lg:text-4xl">
              Organize relacionamento, pipeline e dados de clientes em uma única experiência
            </h1>
            <p className="max-w-3xl text-sm text-slate-600">
              Visualize a jornada completa dos clientes, acompanhe indicadores de conversão e acelere decisões comerciais com
              integrações nativas.
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={openModal}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-500 px-6 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-sky-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200"
            >
              <FaPlus /> Nova oportunidade
            </button>
          </div>
        </div>

        <nav className="mt-8 flex flex-wrap gap-2">
          {CRM_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200 ${
                activeTab === tab.id
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'bg-white/80 text-slate-600 hover:bg-white shadow-sm'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </header>

      <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-64">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por empresa ou oportunidade"
                className="w-full rounded-full border border-slate-200 bg-slate-50 py-2 pl-11 pr-4 text-sm text-slate-700 shadow-inner focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div className="relative">
              <FaFilter className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={ownerFilter}
                onChange={(event) => setOwnerFilter(event.target.value)}
                className="appearance-none rounded-full border border-slate-200 bg-white py-2 pl-9 pr-10 text-sm text-slate-600 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
              >
                <option value="all">Todos os responsáveis</option>
                {owners.map((owner) => (
                  <option key={owner} value={owner}>
                    {owner}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <FaUsers className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={segmentFilter}
                onChange={(event) => setSegmentFilter(event.target.value)}
                className="appearance-none rounded-full border border-slate-200 bg-white py-2 pl-10 pr-10 text-sm text-slate-600 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
              >
                <option value="all">Todos os segmentos</option>
                {segments.map((segment) => (
                  <option key={segment} value={segment}>
                    {segment}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="text-xs text-slate-500">
            Exibindo {filteredDeals.length} de {deals.length} oportunidades cadastradas.
          </div>
        </div>
      </section>

      <div className="space-y-10">
        {activeTab === 'overview' && overviewTabContent}
        {activeTab === 'pipeline' && pipelineTabContent}
        {activeTab === 'accounts' && accountsTabContent}
        {activeTab === 'integrations' && integrationsTabContent}
        {activeTab === 'reports' && reportsTabContent}
      </div>

      <Modal open={integrationModal.open} onClose={closeIntegrationModal}>
        {activeIntegration ? (
          <div className="space-y-6">
            <header className="space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1 text-xs font-medium text-slate-600">
                    <FaShareAlt /> {activeIntegration.name}
                  </span>
                  <h2 className="text-2xl font-semibold text-slate-900">
                    {isManagingIntegration ? 'Gerenciar conexão' : `Conectar ${activeIntegration.name}`}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {isManagingIntegration
                      ? 'Atualize as credenciais utilizadas para sincronizar interações e leads deste canal.'
                      : 'Realize a autenticação e informe os dados para começar a receber leads automaticamente.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openProviderAuth(activeIntegration.id)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <FaExternalLinkAlt /> Abrir {activeIntegration.name}
                </button>
              </div>
            </header>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
              <p className="mb-2 text-sm font-semibold text-slate-700">Como conectar</p>
              <ol className="list-decimal space-y-1 pl-5">
                <li>Autentique-se na conta oficial do canal social e aceite as permissões solicitadas.</li>
                <li>Copie o token ou ID de acesso disponibilizado após a autenticação.</li>
                <li>Informe um e-mail e, se desejar, a URL de webhook para receber eventos em tempo real.</li>
              </ol>
            </div>

            <form onSubmit={submitIntegrationConnection} className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="integration-account" className="text-sm font-medium text-slate-700">
                    Nome da conta
                  </label>
                  <input
                    id="integration-account"
                    name="accountName"
                    value={integrationForm.accountName}
                    onChange={handleIntegrationFormChange}
                    placeholder="Ex.: Equipe Comercial"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="integration-email" className="text-sm font-medium text-slate-700">
                    E-mail de login (opcional)
                  </label>
                  <input
                    id="integration-email"
                    name="loginEmail"
                    type="email"
                    value={integrationForm.loginEmail}
                    onChange={handleIntegrationFormChange}
                    placeholder="contato@empresa.com"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="integration-token" className="text-sm font-medium text-slate-700">
                    Token ou ID de acesso
                  </label>
                  <input
                    id="integration-token"
                    name="accessToken"
                    value={integrationForm.accessToken}
                    onChange={handleIntegrationFormChange}
                    placeholder="Cole aqui o token seguro gerado pela plataforma"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                    required
                  />
                  <p className="text-xs text-slate-400">
                    Utilizaremos o token apenas para sincronizar leads no CRM. Renove-o periodicamente conforme as políticas do
                    provedor.
                  </p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="integration-webhook" className="text-sm font-medium text-slate-700">
                    URL de webhook (opcional)
                  </label>
                  <input
                    id="integration-webhook"
                    name="webhookUrl"
                    type="url"
                    value={integrationForm.webhookUrl}
                    onChange={handleIntegrationFormChange}
                    placeholder="https://app.suaempresa.com.br/webhooks/social"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeIntegrationModal}
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-full bg-sky-500 px-6 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-sky-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200"
                >
                  {isManagingIntegration ? 'Salvar alterações' : 'Conectar canal'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-900">Integração não encontrada</h2>
            <p className="text-sm text-slate-500">
              Atualize a página e tente novamente. Se o problema persistir, contate o administrador da plataforma.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeIntegrationModal}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={modalOpen} onClose={closeModal}>
        <div className="space-y-6">
          <header className="space-y-1">
            <h2 className="text-2xl font-semibold text-slate-900">
              {isEditingDeal ? 'Editar oportunidade' : 'Nova oportunidade'}
            </h2>
            <p className="text-sm text-slate-500">
              Registre as informações essenciais para acompanhar o relacionamento com o cliente.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="crm-name" className="text-sm font-medium text-slate-700">
                  Nome da oportunidade
                </label>
                <input
                  id="crm-name"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  placeholder="Ex.: Implementação plataforma BPM"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="crm-company" className="text-sm font-medium text-slate-700">
                  Empresa
                </label>
                <input
                  id="crm-company"
                  name="company"
                  value={form.company}
                  onChange={handleFormChange}
                  placeholder="Ex.: ACME Corp"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="crm-segment" className="text-sm font-medium text-slate-700">
                  Segmento
                </label>
                <select
                  id="crm-segment"
                  name="segment"
                  value={form.segment}
                  onChange={handleFormChange}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                >
                  {segments.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label htmlFor="crm-owner" className="text-sm font-medium text-slate-700">
                  Responsável
                </label>
                <input
                  id="crm-owner"
                  name="owner"
                  value={form.owner}
                  onChange={handleFormChange}
                  placeholder="Ex.: Ana Oliveira"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="crm-value" className="text-sm font-medium text-slate-700">
                  Valor estimado (R$)
                </label>
                <input
                  id="crm-value"
                  name="value"
                  type="number"
                  min="0"
                  step="500"
                  value={form.value}
                  onChange={handleFormChange}
                  placeholder="Ex.: 25000"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="crm-stage" className="text-sm font-medium text-slate-700">
                  Estágio
                </label>
                <select
                  id="crm-stage"
                  name="stage"
                  value={form.stage}
                  onChange={handleFormChange}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                >
                  {PIPELINE_STAGES.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label htmlFor="crm-next-step" className="text-sm font-medium text-slate-700">
                  Próximo passo
                </label>
                <input
                  id="crm-next-step"
                  name="nextStep"
                  value={form.nextStep}
                  onChange={handleFormChange}
                  placeholder="Ex.: Enviar proposta atualizada"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="crm-next-step-date" className="text-sm font-medium text-slate-700">
                  Data do próximo passo
                </label>
                <input
                  id="crm-next-step-date"
                  name="nextStepDate"
                  type="date"
                  value={form.nextStepDate}
                  onChange={handleFormChange}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="crm-cycle" className="text-sm font-medium text-slate-700">
                  Ciclo estimado (dias)
                </label>
                <input
                  id="crm-cycle"
                  name="cycleDays"
                  type="number"
                  min="0"
                  value={form.cycleDays}
                  onChange={handleFormChange}
                  placeholder="Ex.: 30"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="crm-notes" className="text-sm font-medium text-slate-700">
                Observações
              </label>
              <textarea
                id="crm-notes"
                name="notes"
                value={form.notes}
                onChange={handleFormChange}
                rows={3}
                placeholder="Registre aprendizados, objeções ou recursos prometidos ao cliente."
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-full bg-sky-500 px-6 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-sky-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200"
              >
                {isEditingDeal ? 'Atualizar oportunidade' : 'Salvar oportunidade'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
